import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import {
	isGR6_4SectionA,
	isGR6_4SectionB,
	isGR6_4SectionC,
	isResidentialLandPermitGR6_4
} from '../__index.utils'

export const STEP4_4FIELDS = {
	typesOfWorkBoat: 'typesOfWorkBoat',
	typesOfStructureBoat: 'typesOfStructureBoat',
	typesOfStructureBoatOther: 'typesOfStructureBoatOther',
	areaPier: 'areaPier',
	widthBoatShelter: 'widthBoatShelter',
	depthBoatShelter: 'depthBoatShelter',
	heightBoatShelter: 'heightBoatShelter',
	areaBoatShelter: 'areaBoatShelter',
	boatShelterMaterial: 'boatShelterMaterial',
	boatPierDeclaration: 'boatPierDeclaration',
	areaLandingStage: 'areaLandingStage'
} as const

export function useStep4_4State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR6_4 = isResidentialLandPermitGR6_4(answers?.whatToDo)
	const isSectionA = isGR6_4SectionA(answers?.typesOfStructureBoat!)
	const isSectionB = isGR6_4SectionB(answers?.typesOfStructureBoat!)
	const isSectionC = isGR6_4SectionC(answers?.typesOfStructureBoat!)

	return {
		// Quel(s) type(s) de travaux souhaitez-vous effectuer?
		// Terrain Résidentiel
		typesOfWorkBoat: {
			name: 'typesOfWorkBoat',
			label:
				pageAssets?.request_form_residentialRenovationOrRepairPermit_typesOfWork,
			labelKey: 'request_form_residentialRenovationOrRepairPermit_typesOfWork',
			value: answers?.typesOfWorkBoat?.toString() || '',
			required: isGR6_4,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		// Quel est le type d'installation?
		typesOfStructureBoat: {
			name: 'typesOfStructureBoat',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_structureType,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_structureType',
			value: answers?.typesOfStructureBoat?.toString() || '',
			required: answers?.typesOfWorkBoat?.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.pierOrShelter
			),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		typesOfStructureBoatOther: {
			name: 'typesOfStructureBoatOther',
			label: pageAssets?.request_form_municipalEvaluation_provideMoreInfo_label,
			labelKey: 'request_form_municipalEvaluation_provideMoreInfo_label',
			value: answers?.typesOfStructureBoatOther || '',
			required: isSectionA,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie
		areaPier: {
			name: 'areaPier',
			label: pageAssets?.request_form_surfaceArea,
			labelKey: 'request_form_surfaceArea',
			value: answers?.areaPier?.toString() || '',
			required: isSectionB,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaUnitPier: {
			name: 'areaUnitPier',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaUnitPier?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionB,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Largeur
		widthBoatShelter: {
			name: 'widthBoatShelter',
			label: pageAssets?.request_form_width,
			labelKey: 'request_form_width',
			value: answers?.widthBoatShelter?.toString() || '',
			required: isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		widthUnitBoatShelter: {
			name: 'widthUnitBoatShelter',
			label: pageAssets?.request_form_width_unit,
			labelKey: 'request_form_width_unit',
			value:
				answers?.widthUnitBoatShelter?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionC,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Profondeur
		depthBoatShelter: {
			name: 'depthBoatShelter',
			label: pageAssets?.request_form_buildingTransactionRequest_depth_label,
			labelKey: 'request_form_buildingTransactionRequest_depth_label',
			value: answers?.depthBoatShelter?.toString() || '',
			required: isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		depthUnitBoatShelter: {
			name: 'depthUnitBoatShelter',
			label: pageAssets?.request_form_depth_unit,
			labelKey: 'request_form_depth_unit',
			value:
				answers?.depthUnitBoatShelter?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionC,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Hauteur
		heightBoatShelter: {
			name: 'heightBoatShelter',
			label: pageAssets?.request_form_height,
			labelKey: 'request_form_height',
			value: answers?.heightBoatShelter?.toString() || '',
			required: isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		heightUnitBoatShelter: {
			name: 'heightUnitBoatShelter',
			label: pageAssets?.request_form_height_unit,
			labelKey: 'request_form_height_unit',
			value:
				answers?.heightUnitBoatShelter?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionC,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie
		areaBoatShelter: {
			name: 'areaBoatShelter',
			label: pageAssets?.request_form_surfaceArea,
			labelKey: 'request_form_surfaceArea',
			value: answers?.areaBoatShelter?.toString() || '',
			required: isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaUnitBoatShelter: {
			name: 'areaUnitBoatShelter',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaUnitBoatShelter?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: isSectionC,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Matériel
		boatShelterMaterial: {
			name: 'boatShelterMaterial',
			label: pageAssets?.request_form_landDevWorkPermit_materialProjectedWall,
			labelKey: 'request_form_landDevWorkPermit_materialProjectedWall',
			value: answers?.boatShelterMaterial?.toString() || '',
			required: isSectionC,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie
		areaLandingStage: {
			name: 'areaLandingStage',
			label: pageAssets?.request_form_surfaceArea,
			labelKey: 'request_form_surfaceArea',
			value: answers?.areaLandingStage?.toString() || '',
			required: answers?.typesOfWorkBoat?.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.landingStage
			),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		areaUnitLandingStage: {
			name: 'areaUnitLandingStage',
			label: pageAssets?.request_form_area_unit,
			labelKey: 'request_form_area_unit',
			value:
				answers?.areaUnitLandingStage?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: answers?.typesOfWorkBoat?.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.landingStage
			),
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Declaration
		boatPierDeclaration: {
			name: 'boatPierDeclaration',
			label:
				pageAssets?.request_form_landDevelopmentWorkPermit_boatPierDeclaration,
			labelKey: 'request_form_landDevelopmentWorkPermit_boatPierDeclaration',
			value: answers?.boatPierDeclaration?.toString() || '',
			required: answers?.typesOfWorkBoat?.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.pierOrShelter
			),
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
